import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white hover:from-slate-800 hover:via-slate-700 hover:to-slate-800',
				white: 'bg-white text-accent-foreground hover:bg-accent hover:text-accent-foreground',
				tab: 'bg-gray-200 text-primary hover:bg-gray-200 data-[state=active]:bg-gradient-to-br data-[state=active]:from-slate-900 data-[state=active]:via-slate-800 data-[state=active]:to-slate-900 data-[state=active]:text-white data-[state=active]:shadow shadow',
				destructive: 'bg-destructive text-white hover:bg-destructive/90',
				outline: 'border bg-background hover:bg-accent hover:text-accent-foreground',
				outlineStrong:
					'border-2 border-black bg-background hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				icon: 'h-10 w-10',
				text: 'py-2',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean; // Add isLoading property
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, isLoading = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				disabled={isLoading} // Disable the button when loading
				{...props}
			>
				{isLoading ? (
					<span className="flex items-center space-x-2">
						<Loader2 className="h-6 w-6 animate-spin" />
						<span className="flex items-center gap-2">{props.children}</span>
					</span>
				) : (
					props.children
				)}
			</Comp>
		);
	},
);
Button.displayName = 'Button';

export { Button, buttonVariants };
